import { useTheme } from '@emotion/react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { queryCache, useMutation, useQuery } from 'react-query';

import Block from 'shopper/components/Block';
import Button from 'shopper/components/Button';
import Flex from 'shopper/components/Flex';
import Icon from 'shopper/components/Icon';
import Text from 'shopper/components/Text';

import useMediaQuery from 'hooks/useMediaQuery';

import { sendEvent } from 'lib/gtag';
import { getOfferPageEventCategory } from 'lib/offer';
import { commentQueryKeys, offerQueryKeys, userQueryKeys } from 'lib/queryKeys';
import { get, post } from 'lib/request';
import { scrollToElement } from 'lib/utils';

import { useModal } from 'providers/ModalProvider';
import { useUser } from 'providers/UserProvider';

import ReportDropdown from './ReportDropdown';
import ShareDropdown from './ShareDropdown';

import OFFER from 'constants/offer';

const EditOfferModal = dynamic(() => import('./EditOfferModal'), {
  ssr: false,
});

const ADDITIONAL_DISTANCE_FROM_TOP = 24;

const postOfferLike = (offerId) => post(`/offer/${offerId}/like`);

const getOfferLike = (offerId) => get(`/offer/${offerId}/like`);

/**
 * Offers with status WAITING are only accessible by the user
 * who posted it, shouldn't be able to share. FINISHED offers
 * shouldn't be shared for some reason too.
 */
const isOfferShareableInSocialMedia = (offerStatusName) =>
  offerStatusName !== OFFER.STATUSES.WAITING &&
  offerStatusName !== OFFER.STATUSES.FINISHED;

const OfferMainFooter = ({
  commentsElem,
  offerCommentsCount,
  offerCoupon,
  offerClicks,
  offerId,
  offerInstructions,
  offerLikes,
  offerPrice,
  offerPriceType,
  offerStatusName,
  offerTitle,
  offerWishlistElem,
  userId,
}) => {
  const { showModal } = useModal();
  const { isLg } = useMediaQuery();
  const theme = useTheme();
  const {
    isLoggedIn,
    LoggedInUser,
    loadingLoggedInUser,
    runCallbackIfLoggedIn,
  } = useUser();

  const { data: isOfferLiked } = useQuery(offerQueryKeys.isLiked({ offerId }), {
    queryFn: () => getOfferLike(offerId),
    enabled: isLoggedIn,
  });

  const { data: likesCount } = useQuery(
    offerQueryKeys.likesCount({ offerId }),
    {
      initialData: offerLikes,
      enabled: false,
    }
  );

  const { data: commentsCount } = useQuery(
    commentQueryKeys.offer.count({ offerId }),
    {
      queryFn: () => get(`/offer/${offerId}/comment/count`),
      enabled: false,
      initialData: offerCommentsCount,
    }
  );

  const [mutateOfferLikes] = useMutation(postOfferLike, {
    onSuccess: ({ action }) => {
      // Updates cache used for like on current offer
      queryCache.setQueryData(offerQueryKeys.isLiked({ offerId }), () => ({
        // TODO: remove .data from res on the backend
        data: action === OFFER.ACTIONS.LIKE,
      }));

      queryCache.setQueryData(
        offerQueryKeys.likesCount({ offerId }),
        (prevState) =>
          action === OFFER.ACTIONS.LIKE ? prevState + 1 : prevState - 1
      );

      const offersLikes = queryCache.getQueryData(userQueryKeys.offerLikes());

      // Updates cache used for all offer likes if cache is already set
      if (offersLikes) {
        queryCache.setQueryData(
          userQueryKeys.offerLikes(),
          action === OFFER.ACTIONS.LIKE
            ? [...offersLikes, offerId]
            : offersLikes.filter((likedOfferId) => likedOfferId !== offerId)
        );
      }
    },
  });

  const onCommentsButtonClickHandler = () =>
    scrollToElement(
      commentsElem.offsetTop - ADDITIONAL_DISTANCE_FROM_TOP,
      isLg
    );

  const onNotificationButtonClick = () =>
    scrollToElement(
      offerWishlistElem.offsetTop - ADDITIONAL_DISTANCE_FROM_TOP,
      isLg
    );

  const onScrollToCommentsClickHandler = () => {
    sendEvent({
      action: 'scroll_to_comments',
      category: getOfferPageEventCategory(offerStatusName),
    });
    onCommentsButtonClickHandler();
  };

  const onLikeButtonClickHandler = () => {
    sendEvent({
      action: isLoggedIn ? 'like_attempt' : 'like_attempt_unlogged',
      category: getOfferPageEventCategory(offerStatusName),
    });

    runCallbackIfLoggedIn(() => {
      sendEvent({
        action: isOfferLiked?.data ? 'remove_like_offer' : 'add_like_offer',
        category: getOfferPageEventCategory(offerStatusName),
      });
      mutateOfferLikes(offerId);
    });
  };

  return (
    <Flex
      borderTop="1px solid"
      justifyContent="space-between"
      py={4}
      borderColor={theme.app.offer.dividerBg}
      flexWrap={[null, null, 'wrap']}
      px={[4, null, 6]}
    >
      <Flex alignItems="center">
        <Block display={['none', null, 'flex']}>
          {!loadingLoggedInUser &&
            LoggedInUser?.canEditOffer(userId, offerStatusName) && (
              <Button
                type="subtle"
                size="size3"
                iconLeft={<Icon name="pen" />}
                mr={2}
                onClick={() =>
                  showModal(EditOfferModal, {
                    isOpen: true,
                    offerId,
                    offerInstructions,
                    offerPrice,
                    offerPriceType,
                    offerCoupon,
                  })
                }
              >
                Editar conteúdo
              </Button>
            )}
        </Block>
        <ReportDropdown offerId={offerId} offerStatusName={offerStatusName} />
        {isOfferShareableInSocialMedia(offerStatusName) && (
          <ShareDropdown
            offerPrice={offerPrice}
            offerPriceType={offerPriceType}
            offerStatusName={offerStatusName}
            offerTitle={offerTitle}
          />
        )}
      </Flex>
      <Flex alignItems={['flex-end', null, 'center']}>
        <Flex display={['none', null, 'flex']} alignItems="center">
          <Icon name="visibility" size="size3" mr={2} />
          <Text size="size2">{offerClicks.toLocaleString('pt-BR')}</Text>
        </Flex>
        <Button
          type="subtle"
          size="size3"
          iconLeft={<Icon name="messages" />}
          ml={[null, null, 4]}
          onClick={onScrollToCommentsClickHandler}
        >
          {commentsCount}
        </Button>
        <Button
          size="size3"
          type={isOfferLiked?.data ? 'primary' : 'subtle'}
          iconLeft={<Icon name="like" />}
          ml={2}
          onClick={onLikeButtonClickHandler}
        >
          {likesCount}
        </Button>
        {/* eslint-disable-next-line max-len */}
        {offerWishlistElem &&
          isOfferShareableInSocialMedia(offerStatusName) && (
            <Button
              type="subtle"
              size="size3"
              iconLeft={<Icon name="notification-ring" />}
              mobileIcon={<Icon name="notification-ring" />}
              ml={2}
              onClick={onNotificationButtonClick}
            >
              Ativar Alerta
            </Button>
          )}
      </Flex>
    </Flex>
  );
};

OfferMainFooter.defaultProps = {
  commentsElem: null,
  offerCoupon: null,
  offerClicks: 0,
  offerInstructions: null,
  offerLikes: 0,
  offerWishlistElem: null,
};

OfferMainFooter.propTypes = {
  commentsElem: PropTypes.shape(),
  offerCommentsCount: PropTypes.number.isRequired,
  offerCoupon: PropTypes.string,
  offerClicks: PropTypes.number,
  offerId: PropTypes.number.isRequired,
  offerInstructions: PropTypes.string,
  offerLikes: PropTypes.number,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerTitle: PropTypes.string.isRequired,
  offerWishlistElem: PropTypes.shape(),
  userId: PropTypes.number.isRequired,
};

export default OfferMainFooter;
